import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { PaletteAction } from "../components/CommandPalette";
import type { HelpTopic } from "../components/HelpDrawer";

export interface Crumb {
  label: string;
  /** Omitted for the current page. */
  href?: string;
}

interface ConsoleContextValue {
  openPalette: () => void;
  openHelp: () => void;
  /** Pages set their help topic on mount; cleared on unmount. */
  setHelpTopic: (topic: HelpTopic | null) => void;
  helpTopic: HelpTopic | null;
  /** Pages contribute contextual actions to the command palette (e.g. "New service"). */
  registerActions: (actions: PaletteAction[]) => () => void;
  actions: PaletteAction[];
  /** Detail pages append crumbs after the section crumb derived from the route (e.g. the service name). */
  setCrumbs: (crumbs: Crumb[] | null) => void;
  crumbs: Crumb[] | null;
}

const ConsoleContext = createContext<ConsoleContextValue | null>(null);

export function ConsoleProvider({ children, onOpenPalette, onOpenHelp }: { children: ReactNode; onOpenPalette: () => void; onOpenHelp: () => void }) {
  const [helpTopic, setHelpTopic] = useState<HelpTopic | null>(null);
  const [crumbs, setCrumbs] = useState<Crumb[] | null>(null);
  const [actionSets, setActionSets] = useState<PaletteAction[][]>([]);
  const registerActions = useCallback((actions: PaletteAction[]) => {
    setActionSets((s) => [...s, actions]);
    return () => setActionSets((s) => s.filter((x) => x !== actions));
  }, []);
  const value = useMemo<ConsoleContextValue>(
    () => ({ openPalette: onOpenPalette, openHelp: onOpenHelp, setHelpTopic, helpTopic, registerActions, actions: actionSets.flat(), setCrumbs, crumbs }),
    [onOpenPalette, onOpenHelp, helpTopic, registerActions, actionSets, crumbs],
  );
  return <ConsoleContext.Provider value={value}>{children}</ConsoleContext.Provider>;
}

/** Declares extra breadcrumb segments for the current page (after the section crumb). Cleared on unmount. */
export function useBreadcrumbs(crumbs: Crumb[] | null): void {
  const { setCrumbs } = useConsole();
  const key = crumbs?.map((c) => `${c.label}|${c.href ?? ""}`).join("//") ?? "";
  useEffect(() => {
    setCrumbs(crumbs);
    return () => setCrumbs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setCrumbs]);
}

export function useConsole(): ConsoleContextValue {
  const ctx = useContext(ConsoleContext);
  if (!ctx) throw new Error("useConsole must be used within <ConsoleProvider>");
  return ctx;
}

/** Declares the help topic of the current page. */
export function useHelpTopic(topic: HelpTopic): void {
  const { setHelpTopic } = useConsole();
  const { title, docsAnchor } = topic;
  useEffect(() => {
    setHelpTopic(topic);
    return () => setHelpTopic(null);
    // The body is JSX; re-register only when the identity of the topic changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, docsAnchor, setHelpTopic]);
}

/** Contributes page-level actions to the command palette while the page is mounted. */
export function usePaletteActions(actions: PaletteAction[]): void {
  const { registerActions } = useConsole();
  const ids = actions.map((a) => a.id).join("|");
  useEffect(() => registerActions(actions), [ids, registerActions]); // eslint-disable-line react-hooks/exhaustive-deps
}
